/**
 * Client telemetry seam (PRD Appendix B), now with its sink: Grafana Faro
 * (PLAN §3.6, owner decision 2026-08-09) — open source, self-hosted, received
 * by Alloy and stored in Loki. The seam is unchanged for call sites: `track()`
 * takes the same typed catalogue events, and with no BUN_PUBLIC_FARO_URL the
 * client never opens a socket — the default is exactly the old behaviour.
 *
 * Faro's own instrumentations add the RUM basics for free: unhandled errors,
 * web vitals, session tracking. Browser TRACES (faro-web-tracing) are
 * deliberately not wired: the package drags the whole OTel web SDK through
 * the one-bundle budget (FR-1.2) for spans we do not read yet.
 *
 * NO EVENT HERE CARRIES A HANDLE (ruling R-0918-1; Privacy 2.3 §3.2). Four of
 * them did — `deep_path_offered`, `deep_path_started`, `door_opened`,
 * `knock_started` — and shipped a raw `@handle` from the browser, where the
 * server stream's handle mask (`api/src/analytics/track.ts`) cannot reach it.
 * They are counts of moments in a funnel, not a per-door report, so the field
 * is DROPPED rather than masked: masked it would be the constant
 * `@<handle>` on every row, which is a column of noise sold as a
 * discriminator. `telemetry.test.ts` walks the catalogue and fails on a
 * handle, an address, or any value that reads like one.
 */
import {
  ErrorsInstrumentation,
  SessionInstrumentation,
  ViewInstrumentation,
  WebVitalsInstrumentation,
  initializeFaro,
  type Faro,
} from "@grafana/faro-web-sdk";
import { publicEnv } from "./config";
import type { InboxAction, ProposalTrigger, StandingBand } from "./api";

const FARO_URL = publicEnv(() => process.env.BUN_PUBLIC_FARO_URL) ?? null;

let faro: Faro | null = null;

/** Where the instance survives a dev-server hot reload (see below). */
const FARO_SLOT = "__nobFaro";

/**
 * Idempotent ACROSS MODULE RE-EVALUATION, not just across calls: Bun's dev
 * server re-runs this module on hot reload, which resets `faro` while the
 * SDK's observers from the first init keep running. A second initializeFaro
 * then wires fresh PerformanceObservers to a half-registered instance and
 * they crash on `transports` (seen live 2026-08-09). The instance is parked
 * on the window and reused, so init happens once per PAGE, not once per
 * module lifetime. Without a URL this is a no-op and no socket ever opens.
 */
export function startClientTelemetry(): void {
  if (faro !== null || FARO_URL === null || typeof window === "undefined") return;
  try {
    const slot = window as unknown as Record<string, unknown>;
    const parked = slot[FARO_SLOT] as Faro | undefined;
    if (parked?.api) {
      faro = parked;
      return;
    }
    // NO `session` OPTION, AND THAT IS A LEGAL POSITION RATHER THAN AN
    // OVERSIGHT (ruling R-0919-1; Privacy 2.4 §3.3). The policy now states the
    // Faro default exactly: a session id generated per browser TAB, held in
    // that tab's `sessionStorage`, expiring after 15 minutes idle or 4 hours,
    // discarded when the tab closes, never in `localStorage`. That is the
    // whole reason this product has no cookie banner. Passing
    // `sessionTracking: { persistent: true }` would move the id into
    // `localStorage`, make it survive across visits, and make §3.3 false the
    // same afternoon — at which point consent becomes necessary. So the
    // options object stays as short as it looks, and
    // `telemetry.test.ts` pins both halves: `persistent` never appears here,
    // and nothing Faro-shaped is ever written to `localStorage`.
    faro = initializeFaro({
      url: FARO_URL,
      app: { name: "nob-web" },
      // Explicit, minus PerformanceInstrumentation: its resource-timing
      // PerformanceObserver crashes under Bun's bundling of the SDK
      // ("transports of undefined", seen live 2026-08-09, twice). Errors,
      // web vitals, sessions and views are the RUM that was asked for;
      // resource waterfalls were not worth a crashing observer.
      instrumentations: [
        new ErrorsInstrumentation(),
        new WebVitalsInstrumentation(),
        new SessionInstrumentation(),
        new ViewInstrumentation(),
      ],
    });
    slot[FARO_SLOT] = faro;
  } catch {
    faro = null; // telemetry must never break the app it watches
  }
}

export type TelemetryEvent =
  | {
      name: "onboarding_short_completed";
      elapsed_ms: number;
      chips_selected: number;
      skipped_screens: number;
    }
  /**
   * The wizard's own moments (rulings C5, R-0826-3). They are the funnel §1.7
   * asks about — where an address is lost, trial conversion, and how many
   * knockers ever come back for a door of their own — and each is a step
   * ANSWERED, never a step displayed. Neither email event carries the address:
   * the pair is a drop-off measurement, not a mailing list.
   */
  | { name: "onboarding_trial_started"; tier: string }
  | { name: "onboarding_email_code_sent" }
  | { name: "onboarding_email_done" }
  | { name: "onboarding_door_created" }
  | { name: "onboarding_trial_skipped" }
  | { name: "onboarding_rules_done" }
  | { name: "onboarding_door_deferred" }
  | { name: "doorman_turn" }
  | { name: "setup_answered"; question: string; picks: number }
  | { name: "rules_viewed"; version: number; chars: number }
  /** `author` is the House Rules author the save is attributed to (FR-8.5). */
  | {
      name: "rules_edited";
      version: number;
      author: "recipient" | "nob_proposal";
      chars: number;
      sections_changed: number;
    }
  | {
      name: "rules_rolled_back";
      version: number;
      author: "rollback";
      chars: number;
      sections_changed: number;
      from_version: number;
    }
  // `pack_purchased`, `autoreload_enrolled` and `autoreload_disabled` stood
  // here — Appendix B's three billing events, one per thing a wallet could have
  // done to it. Ruling R-0916-1 deleted the wallet and every purchase that was
  // not a subscription, so the plan events below are the whole of what this
  // product sells and the whole of what it measures selling.
  /**
   * Plans (docs/plans.md §6.3). Instrumented from day one because the whole
   * revenue case rests on an unvalidated conversion rate, and the numbers that
   * would correct it are these: what people start on, which way they move, and
   * when they leave. The TIER is the property; no amount is reported, because
   * the tier names the amount and the billing provider is where money is
   * counted.
   */
  | { name: "plan_started"; tier: string; state: string }
  | { name: "plan_changed"; tier: string; direction: "upgrade" | "downgrade" | "kept" }
  | { name: "plan_cancelled"; tier: string }
  /** Knocking (M8). Nothing about money: there is no ceiling to report and no
   * door price to report it against (ruling C3). */
  | { name: "knock_started" }
  /** One completed exchange of the grilling (M9). The turn is the whole event. */
  | { name: "turn_completed"; turn: number }
  /** The standing screen was shown (M12). The band is the resolution, so it is the property. */
  | { name: "standing_shown"; band: StandingBand }
  /** Inbox (M14, FR-6). The action taken on a knock; feedback rates the ranking. */
  | { name: "inbox_action"; action: InboxAction }
  | { name: "inbox_feedback"; helpful: boolean }
  /**
   * R5a, the console's own guard (PRD §1.7, ruling C7). Two events, deliberately
   * without properties: what R5a asks is how MANY sessions an owner had in a
   * week and whether ANY of them opened the inbox, and both are counts of
   * events per owner. See `components/shell/use-r5a.ts` for why the metric is
   * derived from a denominator and a disqualifier rather than reported as one
   * end-of-session verdict.
   */
  | { name: "console_session_started" }
  | { name: "console_inbox_opened" }
  /**
   * Surfaced rule proposals (M15, FR-8.3). One event per surfaced, accepted or
   * dismissed proposal, carrying what shaped it — the trigger, the number of
   * signals behind it, and whether it had a contradiction to settle.
   */
  | {
      name: "rule_proposal_surfaced";
      trigger_kind: ProposalTrigger;
      signal_count: number;
      had_conflict: boolean;
    }
  | {
      name: "rule_proposal_accepted";
      trigger_kind: ProposalTrigger;
      signal_count: number;
      had_conflict: boolean;
    }
  | {
      name: "rule_proposal_dismissed";
      trigger_kind: ProposalTrigger;
      signal_count: number;
      had_conflict: boolean;
    }
  /**
   * Share artefacts (M17, §1.8 / Appendix B). One event when a card is produced
   * and one when it is actually posted — `posted` is what separates a preview
   * somebody looked at from a loop that closed.
   */
  | { name: "share_card_generated"; kind: ShareCardKind; posted: boolean };

/** The four loops, named as Appendix B names them. */
export type ShareCardKind = "standing" | "door_stats" | "receipt" | "rules";

/**
 * The attributes one event puts on the wire — every declared property, as a
 * string, and nothing else. Extracted from `track` so the leak test can walk
 * the whole catalogue through the function that actually builds the payload
 * rather than through a copy of it (`telemetry.test.ts`).
 */
export function eventAttributes(event: TelemetryEvent): Record<string, string> {
  const attrs: Record<string, string> = {};
  for (const [k, v] of Object.entries(event)) if (k !== "name") attrs[k] = String(v);
  return attrs;
}

export function track(event: TelemetryEvent): void {
  const { name, ...props } = event;
  console.debug(`[nob] ${name}`, props);
  if (faro) {
    try {
      faro.api.pushEvent(name, eventAttributes(event));
    } catch {
      /* never break the app for its own telemetry */
    }
  }
}
